'use client'

import type {
  FileDownloadStatus,
  UseFileDownloadButtonProps,
  UseFileDownloadButtonReturnType,
} from '@/components/file-download-button/file-download-button.types'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'

import { downloadFile } from '@/client/api/download'
import {
  DOWNLOADED_STATUS_DURATION_MS,
  FILE_DOWNLOAD_STATUS_ICONS,
  OBJECT_URL_REVOKE_DELAY_MS,
} from '@/components/file-download-button/file-download-button.constants'
import { useA11y } from '@maestro/core/a11y/a11y.context'
import { createLogger } from '@maestro/core/logger'
import { ApiError } from '@maestro/data-layer'

const log = createLogger('maestro:polar:file-download')

function saveFile(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS)
}

/**
 * Fetches a file generated on the fly (price list, order document) and saves
 * it, so each click produces exactly one generation request and the buyer sees
 * whether it is preparing, downloaded or failed.
 */
export const useFileDownloadButton = (
  props: UseFileDownloadButtonProps
): UseFileDownloadButtonReturnType => {
  const { url, fallbackFileName } = props
  const t = useTranslations('project-scope')
  const { announce } = useA11y()
  const [status, setStatus] = useState<FileDownloadStatus>('idle')
  // State alone lets a second click in the same tick through before re-render.
  const isDownloadingRef = useRef(false)

  useEffect(() => {
    if (status !== 'downloaded') {
      return
    }

    const timeout = setTimeout(
      () => setStatus('idle'),
      DOWNLOADED_STATUS_DURATION_MS
    )
    return () => clearTimeout(timeout)
  }, [status])

  const handleDownload = useCallback(async () => {
    if (isDownloadingRef.current || status === 'downloaded') {
      return
    }

    isDownloadingRef.current = true
    setStatus('preparing')
    announce(t('file-download.preparing'))

    try {
      const file = await downloadFile(url)
      saveFile(file.blob, file.fileName ?? fallbackFileName)
      setStatus('downloaded')
      announce(t('file-download.downloaded'))
    } catch (error) {
      // Redirect (0) and expired session (401): the page is already navigating away.
      if (
        error instanceof ApiError &&
        (error.status === 0 || error.status === 401)
      ) {
        setStatus('idle')
        return
      }

      log.error('[useFileDownloadButton] Download failed', { error, url })
      setStatus('failed')
      announce(t('file-download.failed'))
    } finally {
      isDownloadingRef.current = false
    }
  }, [url, fallbackFileName, status, announce, t])

  return {
    status,
    statusIcon: FILE_DOWNLOAD_STATUS_ICONS[status],
    isInactive: status === 'preparing' || status === 'downloaded',
    handleDownload,
  }
}
